import {
  ESlaLevel,
  NEW_DELIVERY_MINUTES,
  SLA_CRITICAL_MINUTES,
  SLA_WARNING_MINUTES,
} from './constants';
import type {
  MonitorDelivery,
  MonitorDeliveryAddress,
  MonitorDeliveryDetailMetrics,
} from './types';

const MINUTES_IN_HOUR = 60;
const HOURS_IN_DAY = 24;
const MINUTES_IN_DAY = MINUTES_IN_HOUR * HOURS_IN_DAY;

export const formatElapsedTime = (elapsedMinutes: number) => {
  const minutes = Math.max(0, Math.floor(elapsedMinutes));

  if (minutes < MINUTES_IN_HOUR) {
    return `${minutes}m`;
  }

  if (minutes < MINUTES_IN_DAY) {
    const hours = Math.floor(minutes / MINUTES_IN_HOUR);
    const remainingMinutes = String(minutes % MINUTES_IN_HOUR).padStart(2, '0');

    return `${hours}h ${remainingMinutes}m`;
  }

  const days = Math.floor(minutes / MINUTES_IN_DAY);
  const remainingHours = Math.floor(
    (minutes % MINUTES_IN_DAY) / MINUTES_IN_HOUR,
  );

  return `${days}d ${String(remainingHours).padStart(2, '0')}h`;
};

export const formatDeliverymanName = (name: string) => {
  const [firstName = '', secondName] = name.trim().split(/\s+/);

  return secondName
    ? `${firstName} ${secondName.charAt(0).toUpperCase()}.`
    : firstName;
};

export const getSlaLevel = (elapsedMinutes: number) => {
  if (elapsedMinutes >= SLA_CRITICAL_MINUTES) {
    return ESlaLevel.CRITICAL;
  }

  if (elapsedMinutes >= SLA_WARNING_MINUTES) {
    return ESlaLevel.WARNING;
  }

  return ESlaLevel.OK;
};

const SLA_LEVEL_SEVERITY: Record<ESlaLevel, number> = {
  [ESlaLevel.OK]: 0,
  [ESlaLevel.WARNING]: 1,
  [ESlaLevel.CRITICAL]: 2,
};

const projectSlaLevel = (slaLevel: ESlaLevel, elapsedMinutes: number) => {
  const projectedSlaLevel = getSlaLevel(elapsedMinutes);

  return SLA_LEVEL_SEVERITY[projectedSlaLevel] > SLA_LEVEL_SEVERITY[slaLevel]
    ? projectedSlaLevel
    : slaLevel;
};

export const projectMonitorDetailMetrics = (
  metrics: MonitorDeliveryDetailMetrics,
  minutesSinceUpdate: number,
): MonitorDeliveryDetailMetrics => {
  if (minutesSinceUpdate <= 0) {
    return metrics;
  }

  const elapsedMinutes = metrics.elapsedMinutes + minutesSinceUpdate;

  return {
    ...metrics,
    elapsedMinutes,
    slaLevel: projectSlaLevel(metrics.slaLevel, elapsedMinutes),
    currentStepMinutes:
      metrics.currentStepMinutes === null
        ? null
        : metrics.currentStepMinutes + minutesSinceUpdate,
  };
};

export const projectMonitorDelivery = (
  delivery: MonitorDelivery,
  minutesSinceUpdate: number,
): MonitorDelivery => {
  if (minutesSinceUpdate <= 0) {
    return delivery;
  }

  const elapsedMinutes = delivery.elapsedMinutes + minutesSinceUpdate;

  return {
    ...delivery,
    elapsedMinutes,
    slaLevel: projectSlaLevel(delivery.slaLevel, elapsedMinutes),
    isNew: delivery.isNew && elapsedMinutes < NEW_DELIVERY_MINUTES,
  };
};

export const getWhatsappUrl = (phone: string) =>
  `https://wa.me/${phone.replace(/\D/g, '')}`;

export const formatMonitorAddress = ({
  street,
  number,
  district,
}: MonitorDeliveryAddress) =>
  [`${street}, ${number || 's/n'}`, district].filter(Boolean).join(' - ');
